// src/pages/Village/ViewVillages.jsx
import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import villageService from '../../services/Villages.service';
import Toast from '../../components/Toast/Toast';
import './ViewVillages.css';

const ViewVillages = () => {
  const navigate = useNavigate();
  const [villages, setVillages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [editId, setEditId] = useState(null);
  const [editedName, setEditedName] = useState('');
  const [toast, setToast] = useState(null);
  const itemsPerPage = 10;

  const loadVillages = async () => {
    setLoading(true);
    try {
      const res = await villageService.getAllVillages();
      if (res.success) setVillages(res.data || []);
    } catch (err) {
      setToast({ message: 'Failed to load villages', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadVillages(); }, []);

  const filteredVillages = useMemo(() => {
    if (!searchQuery) return villages;
    const q = searchQuery.toLowerCase();
    return villages.filter(v =>
      v.name?.toLowerCase().includes(q) ||
      v.headquarter?.name?.toLowerCase().includes(q) ||
      v.region?.name?.toLowerCase().includes(q) ||
      v.company?.name?.toLowerCase().includes(q)
    );
  }, [searchQuery, villages]);

  const totalPages = Math.ceil(filteredVillages.length / itemsPerPage) || 1;
  const startIdx = (currentPage - 1) * itemsPerPage;
  const pageItems = filteredVillages.slice(startIdx, startIdx + itemsPerPage);

  const handleEditClick = (v) => { setEditId(v.id); setEditedName(v.name); };
  const handleCancelEdit = () => { setEditId(null); setEditedName(''); };

  const handleSaveEdit = async (id) => {
    if (!editedName.trim()) {
      setToast({ message: 'Name cannot be empty', type: 'error' });
      return;
    }
    try {
      const res = await villageService.updateVillage(id, { name: editedName.trim() });
      if (res.success) {
        setVillages(prev => prev.map(v => (v.id === id ? { ...v, name: editedName.trim() } : v)));
        setToast({ message: 'Village updated', type: 'success' });
        handleCancelEdit();
      }
    } catch (err) {
      setToast({
        message: err.response?.data?.message || 'Update failed',
        type: 'error',
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this village?')) return;
    try {
      const res = await villageService.deleteVillage(id);
      if (res.success) {
        setVillages(prev => prev.filter(v => v.id !== id));
        setToast({ message: 'Village deleted', type: 'success' });
      }
    } catch (err) {
      setToast({
        message: err.response?.data?.message || 'Delete failed',
        type: 'error',
      });
    }
  };

  return (
    <div className="village-dashboard-container">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="breadcrumb">
        <a href="/dashboard">Dashboard</a> / <span>Villages</span>
      </div>

      <div className="page-header">
        <h1 className="page-title">Villages List</h1>
        <button className="btn btn-primary" onClick={() => navigate('/village/add')}>
          + Add New Village
        </button>
      </div>

      <div className="search-container">
        <input
          type="text"
          className="search-input-control"
          placeholder="Search by village, headquarter, region, or company..."
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
        />
      </div>

      <div className="table-container">
        <table className="village-table">
          <thead>
            <tr>
              <th>NAME</th>
              <th>HEADQUARTER</th>
              <th>REGION</th>
              <th>COMPANY</th>
              <th>STATUS</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="6" className="text-center">Loading...</td></tr>
            ) : pageItems.length > 0 ? pageItems.map(v => (
              <tr key={v.id}>
                <td>
                  {editId === v.id ? (
                    <div className="edit-cell-wrapper">
                      <input
                        type="text"
                        className="edit-input"
                        value={editedName}
                        onChange={(e) => setEditedName(e.target.value)}
                      />
                      <button className="save-btn" onClick={() => handleSaveEdit(v.id)}>Save</button>
                      <button className="cancel-btn" onClick={handleCancelEdit}>Cancel</button>
                    </div>
                  ) : (
                    <span className="village-name-text">{v.name}</span>
                  )}
                </td>
                <td>{v.headquarter?.name || '—'}</td>
                <td>{v.region?.name || '—'}</td>
                <td>{v.company?.name || '—'}</td>
                <td>
                  <span className={`badge ${v.status?.toLowerCase() === 'active' ? 'badge-success' : 'badge-danger'}`}>
                    {v.status}
                  </span>
                </td>
                <td className="actions-cell">
                  {editId !== v.id && (
                    <>
                      <button className="action-btn edit-btn" onClick={() => handleEditClick(v)}>edit</button>
                      <button className="action-btn delete-btn" onClick={() => handleDelete(v.id)}>🗑️</button>
                    </>
                  )}
                </td>
              </tr>
            )) : (
              <tr><td colSpan="6" className="text-center">No villages found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination-wrapper">
        <div className="pagination-info">
          Showing {pageItems.length === 0 ? 0 : startIdx + 1}-{startIdx + pageItems.length} of {filteredVillages.length} items.
        </div>
        <div className="pagination-buttons">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="btn-pagination"
          >
            Prev
          </button>
          <span className="page-indicator">Page {currentPage} of {totalPages}</span>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage >= totalPages}
            className="btn-pagination"
          >
            Next »
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewVillages;